import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AIProgressModal from './AIProgressModal';
import { MeasureFilesField, MeasureLinkField, measureToForm } from './MeasureFields';
import { SalesProjectLinkField } from './SalesProjectLinkField';
import { projectToForm } from './soldProjects';

// userRole: admin/general_manager podem criar MEASURE (medição: sem equipe, só eles veem). fromMeasure: medição que vira instalação (pré-preenche + vínculo). fromProject: projeto vendido (Portal de Vendas) que vira instalação
const AddEditWorkModal = ({ isOpen, onClose, work, date, onSuccess, onAIProcess, language = 'pt-BR', userRole, fromMeasure, fromProject }) => {
  const isEdit = !!work;
  const canMeasure = userRole === 'admin' || userRole === 'general_manager';
  const [loading, setLoading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfUploadError, setPdfUploadError] = useState('');
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [pdfConflicts, setPdfConflicts] = useState([]);
  const [pdfExtractedData, setPdfExtractedData] = useState({});
  const [currentPdfFileId, setCurrentPdfFileId] = useState(null);
  
  // Modal de Progresso da IA
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiLogs, setAiLogs] = useState([]);
  const [aiComplete, setAiComplete] = useState(false);
  const [aiExtractedData, setAiExtractedData] = useState(null);
  
  // Flag para prevenir useEffect de sobrescrever dados da IA
  const [aiDataLoaded, setAiDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    teams: [],  // Multi-equipe: 1 ou mais equipes
    date: '',
    duration_days: 1,
    customer_name: '',
    customer_email: '',
    order_number: '',
    tech_measure: '',  // NOVO - Técnico que fez a medição
    start_time: '',
    doors: 0,
    windows: 0,
    address: '',
    city: '',
    zip_code: '',
    year_built: '',  // NOVO - Ano de construção
    job_type: 'INSTALL',
    draft_pdf_url: '',
    service_notes: '',  // NOVO: Notas para SERVICE
    measure_notes: '',  // MEASURE: notas da medição
    measure_files: [],  // MEASURE: anexos [{file_id, filename, content_type, size}]
    measure_job_id: null,  // INSTALL/SERVICE criado a partir de uma medição
    sales_request_id: null,  // INSTALL de um projeto vendido (Portal de Vendas)
    sales_contract_number: '',
    // Campos de Logística
    load_city: '',
    load_door: '',
    load_time: '',
    supervisor: '',
    project_coordinator: '',
    amount_due: '',
    payment_type: '',
    financing: ''
  });

  // Funções de normalização
  const normalizeTime = (timeStr) => {
    if (!timeStr || timeStr === 'None' || timeStr === 'null' || timeStr === 'undefined') return '';
    
    // Remove espaços e caracteres especiais
    let cleaned = timeStr.toString().replace(/[^0-9:]/g, '');
    
    // Se vazio após limpeza, retorna vazio
    if (!cleaned) return '';
    
    // Se já está no formato HH:MM, retorna
    if (/^\d{2}:\d{2}$/.test(cleaned)) {
      return cleaned;
    }
    
    // Remove : se houver
    cleaned = cleaned.replace(':', '');
    
    // Casos:
    // 1 dígito: 7 → 07:00
    if (cleaned.length === 1) {
      return `0${cleaned}:00`;
    }
    // 2 dígitos: 15 → 15:00, 07 → 07:00
    else if (cleaned.length === 2) {
      return `${cleaned}:00`;
    }
    // 3 dígitos: 830 → 08:30
    else if (cleaned.length === 3) {
      return `0${cleaned[0]}:${cleaned.substring(1)}`;
    }
    // 4 dígitos: 0830 → 08:30
    else if (cleaned.length === 4) {
      return `${cleaned.substring(0, 2)}:${cleaned.substring(2)}`;
    }
    
    return ''; // Retorna vazio se não conseguir normalizar
  };
  
  const normalizeAmount = (amountStr) => {
    if (!amountStr || amountStr === 'None' || amountStr === 'null' || amountStr === 'undefined') return '';
    
    // Remove $ duplicados - mantém apenas 1
    let cleaned = amountStr.toString().trim();
    
    // Se vazio, retorna vazio
    if (!cleaned) return '';
    
    // Se tem múltiplos $, remove os extras
    if (cleaned.match(/\$\$+/)) {
      cleaned = cleaned.replace(/\$+/g, '$'); // Substitui múltiplos $ por 1 só
    }
    
    // Se não tem $, adiciona (apenas se tiver número)
    if (!cleaned.startsWith('$') && cleaned.match(/\d/)) {
      cleaned = '$' + cleaned;
    }
    
    return cleaned;
  };

  // Helper para adicionar log no modal de IA
  const addAILog = (icon, message, type = 'info') => {
    setAiLogs(prev => [...prev, { icon, message, type, timestamp: new Date() }]);
  };

  useEffect(() => {
    if (work && isOpen && !aiDataLoaded) {
      // Modo edição - normalizar dados ao carregar
      // APENAS se não temos dados recém-extraídos pela IA
      setFormData({
        teams: (work.teams && work.teams.length) ? work.teams : (work.team ? [work.team] : []),
        date: work.date || '',
        duration_days: work.duration_days || 1,
        customer_name: work.customer_name || '',
        customer_email: work.customer_email || '',
        order_number: work.order_number || '',
        tech_measure: work.tech_measure || '',  // Tech Measure
        start_time: work.time || '',
        doors: work.doors || 0,
        windows: work.windows || 0,
        address: work.address || '',
        city: work.city || '',
        zip_code: work.zip_code || '',
        year_built: work.year_built || '',  // Ano de construção
        job_type: work.job_type || 'INSTALL',
        draft_pdf_url: work.draft_pdf_url || '',
        service_notes: work.service_notes || '',
        measure_notes: work.measure_notes || '',
        measure_files: work.measure_files || [],
        measure_job_id: work.measure_job_id || null,
        sales_request_id: work.sales_request_id || null,
        sales_contract_number: work.sales_contract_number || '',
        // Logística - com normalização
        load_city: work.load_city || '',
        load_door: work.load_door || '',
        load_time: normalizeTime(work.load_time || ''),  // Normalizar horário
        supervisor: work.supervisor || '',
        project_coordinator: work.project_coordinator || '',
        amount_due: normalizeAmount(work.amount_due || ''),  // Normalizar valor
        payment_type: work.payment_type || '',
        financing: work.financing || ''
      });
    } else if (date && isOpen && !aiDataLoaded) {
      // Modo criar - data pré-preenchida (usar data local, não UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDate = `${year}-${month}-${day}`;
      
      setFormData({
        teams: [], // Admin escolhe 1 ou mais equipes
        date: localDate,
        duration_days: 1,
        customer_name: '',
        order_number: '',
        tech_measure: '',  // Tech Measure
        start_time: '08:00',  // Horário padrão: 8:00 AM
        doors: 0,
        windows: 0,
        address: '',
        city: '',
        zip_code: '',
        year_built: '',  // Ano de construção
        job_type: 'INSTALL',
        draft_pdf_url: '',
        service_notes: '',  // Notas do SERVICE
        measure_notes: '',
        measure_files: [],
        measure_job_id: null,
        sales_request_id: null,
        sales_contract_number: '',
        load_city: '',
        load_door: '',
        load_time: '',
        supervisor: '',
        project_coordinator: '',
        amount_due: '',
        payment_type: '',
        financing: '',
        ...(fromMeasure ? measureToForm(fromMeasure) : {}),  // instalação a partir de uma medição
        ...(fromProject ? projectToForm(fromProject) : {}),  // instalação de um projeto vendido
      });
    }
    
    // Resetar flag quando modal fecha
    if (!isOpen && aiDataLoaded) {
      setAiDataLoaded(false);
    }
  }, [work, date, isOpen, aiDataLoaded, fromMeasure, fromProject]);

  const handlePdfUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset input value so escolher o MESMO arquivo de novo dispara o onChange
    try { e.target.value = ''; } catch (_) {}
    setPdfUploadError('');
    if (!file) return;

    // Aceita por MIME ou por extensão .pdf (alguns navegadores/SO não mandam o MIME)
    const nameLower = (file.name || '').toLowerCase();
    const isPdf = (file.type && file.type.toLowerCase().includes('pdf')) || nameLower.endsWith('.pdf');
    if (!isPdf) {
      const msg = language === 'pt-BR' ? 'Selecione um arquivo PDF (.pdf)' : 'Please select a PDF file (.pdf)';
      setPdfUploadError(msg);
      toast.error(msg);
      return;
    }

    setUploadingPdf(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // IMPORTANTE: usamos fetch nativo (não axios) e NÃO definimos Content-Type.
      // Assim o navegador gera o cabeçalho multipart com o boundary correto em
      // TODOS os navegadores (Safari incluso). Definir Content-Type manualmente
      // quebra o boundary e o servidor responde 422 "Field required".
      const resp = await fetch(`${API_URL}/api/schedules/upload-draft`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      });

      if (!resp.ok) {
        let detailTxt = '';
        try {
          const j = await resp.json();
          const d = j && j.detail;
          if (Array.isArray(d)) detailTxt = d.map(x => (x && x.msg) ? x.msg : JSON.stringify(x)).join('; ');
          else if (d && typeof d === 'object') detailTxt = JSON.stringify(d);
          else detailTxt = d || '';
        } catch (_) { /* corpo não-JSON */ }
        throw new Error(detailTxt || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      const fid = data && data.file_id;
      if (!fid) {
        throw new Error('No file_id returned');
      }
      setFormData(prev => ({ ...prev, draft_pdf_url: fid }));
      setPdfUploadError('');
      toast.success(language === 'pt-BR' ? '✅ PDF carregado!' : '✅ PDF uploaded!');
      
    } catch (error) {
      console.error('Erro upload:', error);
      const detail = (error && error.message) ? error.message : '';
      const msg = (language === 'pt-BR' ? 'Erro ao carregar o PDF. Tente novamente.' : 'Failed to upload PDF. Try again.') + (detail ? ` (${detail})` : '');
      setPdfUploadError(msg);
      toast.error(msg);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSaveWithAI = async () => {
    if (!formData.draft_pdf_url) {
      toast.error(language === 'pt-BR' ? 'Faça upload do PDF primeiro' : 'Upload PDF first');
      return;
    }
    if (!formData.teams || formData.teams.length === 0 || !formData.date) {
      toast.error(language === 'pt-BR' ? 'Selecione ao menos uma equipe e a data' : 'Select at least one team and the date');
      return;
    }

    const token = sessionStorage.getItem('access_token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // Abrir modal de progresso da IA
    setShowAIModal(true);
    setAiLogs([]);
    setAiComplete(false);
    setAiExtractedData(null);

    // Dados extraídos (IA ou regex). Fica null se nada funcionar — MAS o trabalho
    // é salvo mesmo assim, com o que o usuário já preencheu (best-effort).
    let extracted = null;

    addAILog('📄', 'PDF carregado com sucesso');
    addAILog('🤖', 'Enviando para IA (Gemini 2.5 Flash)...');
    addAILog('🧠', 'IA analisando documento...');

    // 1) Tentar extração com IA (com timeout p/ não travar se o servidor demorar)
    try {
      const aiResp = await axios.post(
        `${API_URL}/api/pdf/extract-data-ai`,
        { pdf_file_id: formData.draft_pdf_url },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      if (aiResp?.data?.success && aiResp?.data?.extracted_data) {
        extracted = aiResp.data.extracted_data;
        addAILog('✅', language === 'pt-BR' ? 'IA extraiu dados com sucesso!' : 'AI extracted data!', 'success');
      } else {
        addAILog('⚠️', language === 'pt-BR' ? 'IA não conseguiu ler — usando leitura rápida...' : 'AI could not read — using fast reader...', 'error');
      }
    } catch (aiError) {
      console.warn('⚠️ AI extraction failed:', aiError);
      addAILog('⚠️', language === 'pt-BR' ? 'IA demorou — usando leitura rápida...' : 'AI slow — using fast reader...', 'error');
    }

    // 2) Fallback regex (rápido) — só se a IA não trouxe dados
    if (!extracted) {
      try {
        const rxResp = await axios.post(
          `${API_URL}/api/pdf/extract-data`,
          formData.draft_pdf_url,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 20000 }
        );
        if (rxResp?.data?.success && rxResp?.data?.extracted_data) {
          extracted = rxResp.data.extracted_data;
          addAILog('📝', language === 'pt-BR' ? 'Dados extraídos (leitura rápida)' : 'Data extracted (fast reader)', 'info');
        }
      } catch (rxError) {
        console.warn('⚠️ Regex extraction failed:', rxError);
      }
    }

    // 3) Merge best-effort — nunca apaga o que o usuário já digitou
    const pick = (aiVal, current) => {
      if (aiVal === null || aiVal === undefined) return current;
      if (typeof aiVal === 'string' && aiVal.trim() === '') return current;
      return aiVal;
    };
    let merged = { ...formData };
    if (extracted) {
      merged = {
        ...formData,
        customer_name: pick(extracted.customer_name, formData.customer_name),
        customer_email: pick(extracted.customer_email, formData.customer_email),
        order_number: pick(extracted.order_number, formData.order_number),
        tech_measure: pick(extracted.tech_measure, formData.tech_measure),
        duration_days: pick(extracted.duration_days, formData.duration_days) || formData.duration_days || 1,
        address: pick(extracted.address, formData.address),
        city: pick(extracted.city, formData.city),
        zip_code: pick(extracted.zip_code, formData.zip_code),
        year_built: pick(extracted.year_built, formData.year_built),
        doors: extracted.doors !== undefined && extracted.doors !== null ? extracted.doors : (formData.doors || 0),
        windows: extracted.windows !== undefined && extracted.windows !== null ? extracted.windows : (formData.windows || 0),
      };
      setAiDataLoaded(true);
      setFormData(merged);
      setAiExtractedData(extracted);
      addAILog('📋', language === 'pt-BR' ? 'Formulário preenchido automaticamente!' : 'Form auto-filled!', 'success');
    } else {
      addAILog('ℹ️', language === 'pt-BR' ? 'Não deu pra ler o PDF. Salvando com os dados preenchidos.' : 'Could not read PDF. Saving with entered data.', 'info');
    }

    // Garante que o trabalho SEMPRE salva: se ninguém preencheu o nome, usa um provisório.
    if (!merged.customer_name || !String(merged.customer_name).trim()) {
      merged = { ...merged, customer_name: merged.order_number ? `Order ${merged.order_number}` : (language === 'pt-BR' ? 'Novo Trabalho (revisar)' : 'New Job (review)') };
    }

    // 4) SEMPRE salvar (best-effort). handleSubmit valida nome/equipe/data e avisa se faltar.
    setAiComplete(true);
    addAILog('💾', language === 'pt-BR' ? 'Salvando trabalho...' : 'Saving job...', 'info');
    setShowAIModal(false);
    setTimeout(() => handleSubmit(merged), 300);
  };

  const handleSubmit = async (overrideData) => {
    // Usa dados explícitos (passados pelo handleSaveWithAI após merge) ou cai no state atual
    const data = overrideData && typeof overrideData === 'object' && !overrideData.preventDefault ? overrideData : formData;
    const isMeasure = data.job_type === 'MEASURE';
    if (!data.customer_name || !data.date || (!isMeasure && (!data.teams || data.teams.length === 0))) {
      toast.error(isMeasure
        ? (language === 'pt-BR' ? 'Informe nome e data' : 'Enter name and date')
        : (language === 'pt-BR' ? 'Selecione equipe(s), nome e data' : 'Select team(s), name and date'));
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Gerar fingerprint simples
      const fingerprint = `${data.customer_name}_${data.date}_${data.order_number}_${Date.now()}`;

      const jobData = {
        team: isMeasure ? null : data.teams[0],
        teams: isMeasure ? [] : data.teams,
        date: data.date,
        duration_days: parseInt(data.duration_days),
        card_key: work?.card_key || `manual_${Date.now()}`,
        customer_name: data.customer_name,
        customer_email: data.customer_email,
        order_number: data.order_number,
        tech_measure: data.tech_measure,  // Tech Measure
        time: data.start_time,
        doors: parseInt(data.doors) || 0,
        windows: parseInt(data.windows) || 0,
        address: data.address,
        city: data.city,
        zip_code: data.zip_code,
        year_built: data.year_built,  // Ano de construção
        job_type: data.job_type,
        draft_pdf_url: data.draft_pdf_url,
        has_pdf: !!data.draft_pdf_url,  // true se tem PDF
        service_notes: data.service_notes,  // Notas do SERVICE
        measure_notes: isMeasure ? data.measure_notes : (data.measure_notes || null),
        measure_files: isMeasure ? (data.measure_files || []) : [],
        measure_job_id: isMeasure ? null : (data.measure_job_id || null),
        sales_request_id: isMeasure ? null : (data.sales_request_id || null),
        sales_contract_number: isMeasure ? null : (data.sales_contract_number || null),
        source: work?.source || 'manual',
        fingerprint: fingerprint,
        // Logística - com normalização
        load_city: data.load_city,
        load_door: data.load_door,
        load_time: normalizeTime(data.load_time),  // Normalizar horário ao salvar
        supervisor: data.supervisor,
        project_coordinator: data.project_coordinator,
        amount_due: normalizeAmount(data.amount_due),  // Normalizar valor ao salvar
        payment_type: data.payment_type,
        financing: data.financing
      };

      if (isEdit) {
        // Editar
        await axios.patch(`${API_URL}/api/schedules/${work.id}`, jobData);
        toast.success(language === 'pt-BR' ? '✅ Trabalho atualizado!' : '✅ Work updated!');
        // 1) Mostra a alteração na hora: atualiza a lista e fecha o modal PRIMEIRO
        try { await onSuccess(); } catch (_) {}
        onClose();
        // 2) Só então dispara o IA do Labor Bill em BACKGROUND (não bloqueia a exibição)
        if (data.draft_pdf_url && !work.draft_pdf_url && onAIProcess) {
          setTimeout(() => { try { onAIProcess(work.id); } catch (_) {} }, 150);
        }
      } else {
        // Criar
        const response = await axios.post(`${API_URL}/api/schedules`, jobData);
        toast.success(language === 'pt-BR' ? '✅ Trabalho criado!' : '✅ Work created!');
        const newJobId = response?.data?.id || response?.data?.job_id;
        // 1) Mostra o trabalho na hora: atualiza a lista e fecha o modal PRIMEIRO
        try { await onSuccess(); } catch (_) {}
        onClose();
        // 2) Só então dispara o IA do Labor Bill em BACKGROUND (não bloqueia a exibição)
        if (data.draft_pdf_url && newJobId && onAIProcess) {
          setTimeout(() => { try { onAIProcess(newJobId); } catch (_) {} }, 150);
        }
      }
    } catch (error) {
      console.error('Erro:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (language === 'pt-BR' ? 'Erro ao salvar' : 'Error saving'));
    } finally {
      setLoading(false);
    }
  };

  const handlePdfConflictResolution = async (mode) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('access_token');
      
      // Preparar decisões
      const decisions = {};
      
      if (mode === 'replace_all') {
        // Substituir todos
        pdfConflicts.forEach(conflict => {
          decisions[conflict.field] = 'replace';
        });
      } else if (mode === 'skip_all') {
        // Pular todos
        pdfConflicts.forEach(conflict => {
          decisions[conflict.field] = 'skip';
        });
      } else {
        // Individual - usar decisões marcadas
        pdfConflicts.forEach(conflict => {
          decisions[conflict.field] = conflict.decision || 'skip';
        });
      }
      
      // Enviar decisões para backend
      const response = await axios.post(
        `${API_URL}/api/schedules/${work.id}/resolve-pdf-conflicts`,
        {
          decisions,
          extracted_data: pdfExtractedData,
          pdf_file_id: currentPdfFileId
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success(
          language === 'pt-BR' ?
            `✅ ${response.data.replaced} campos atualizados!` :
            `✅ ${response.data.replaced} fields updated!`
        );
        
        // Atualizar formulário com dados do PDF (apenas campos com replace)
        const newFormData = { ...formData };
        Object.keys(decisions).forEach(field => {
          if (decisions[field] === 'replace' && pdfExtractedData[field] !== undefined) {
            newFormData[field] = pdfExtractedData[field];
          }
        });
        setFormData(newFormData);
        
        // Fechar modal de conflitos
        setShowConflictModal(false);
        setPdfConflicts([]);
        
        // Recarregar para pegar dados atualizados
        if (work?.id) {
          onSuccess();
        }
      }
      
    } catch (error) {
      console.error('Erro ao resolver conflitos:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao aplicar mudanças' : 'Error applying changes');
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? '✏️ ' : '➕ '}
            {language === 'pt-BR' ? (isEdit ? 'Editar Trabalho' : 'Adicionar Trabalho') : (isEdit ? 'Edit Work' : 'Add Work')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo de Trabalho: INSTALL, SERVICE ou MEASURE (medição: só Admin/GM) */}
          <div>
            <Label>{language === 'pt-BR' ? 'Tipo de Trabalho' : 'Work Type'} *</Label>
            <div className="flex gap-4 mt-2 flex-wrap" data-testid="work-type-group">
              {[['INSTALL', '🔧 INSTALL'], ['SERVICE', '🔧 SERVICE'], ...(canMeasure || formData.job_type === 'MEASURE' ? [['MEASURE', '📏 MEASURE']] : [])].map(([val, lbl]) => (
                <label key={val} className={`flex items-center gap-2 cursor-pointer ${val === 'MEASURE' ? 'text-purple-800' : ''}`}>
                  <input
                    type="radio"
                    name="job_type"
                    value={val}
                    checked={formData.job_type === val}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                    className="w-4 h-4"
                    data-testid={`work-type-${val}`}
                  />
                  <span className="text-sm font-medium">{lbl}</span>
                </label>
              ))}
            </div>
            {formData.job_type === 'MEASURE' && (
              <p className="text-xs text-purple-700 mt-1.5 bg-purple-50 border border-purple-200 rounded-md px-2 py-1.5" data-testid="work-measure-hint">
                📏 {language === 'pt-BR' ? 'Medição: sem equipe — só o Admin/Gerente vê. Quando virar instalação, crie o trabalho INSTALL a partir dela (card "Trabalhos medidos").' : 'Measurement: no team — only Admin/Manager can see it. When it becomes an install, create the INSTALL job from it ("Measured jobs" card).'}
              </p>
            )}
          </div>

          {/* Seleção de Equipe(s) — 1 ou várias (multi-equipe). MEASURE não tem equipe. */}
          {formData.job_type !== 'MEASURE' && (
          <div>
            <Label>{language === 'pt-BR' ? 'Equipe(s)' : 'Team(s)'} *</Label>
            <p className="text-xs text-zinc-500 mb-1">
              {language === 'pt-BR' ? 'Marque 1 ou mais equipes. Trabalhos com 2+ equipes aparecem no schedule de todas.' : "Check 1 or more teams. Jobs with 2+ teams appear in every team's schedule."}
            </p>
            <div className="grid grid-cols-5 gap-2">
              {Array.from({length: 10}, (_, i) => `PM${i + 1}`).map(team => {
                const checked = (formData.teams || []).includes(team);
                return (
                  <label
                    key={team}
                    data-testid={`work-team-checkbox-${team}`}
                    className={`flex items-center justify-center border rounded-lg px-2 py-1.5 text-sm cursor-pointer select-none transition-colors ${checked ? 'bg-indigo-600 border-indigo-600 text-white font-semibold' : 'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50'}`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={checked}
                      onChange={() => {
                        setFormData(prev => {
                          const cur = prev.teams || [];
                          const next = cur.includes(team) ? cur.filter(t => t !== team) : [...cur, team];
                          return { ...prev, teams: next };
                        });
                      }}
                    />
                    {team}
                  </label>
                );
              })}
            </div>
            {(formData.teams || []).length > 1 && (
              <p className="text-xs text-indigo-700 mt-1 font-semibold" data-testid="work-multiteam-note">
                👥 {language === 'pt-BR' ? 'Multi-equipe' : 'Multi-team'}: {formData.teams.join(', ')}
              </p>
            )}
          </div>
          )}

          {/* INSTALL de um projeto vendido no Portal de Vendas */}
          {formData.job_type !== 'MEASURE' && formData.sales_request_id && (
            <SalesProjectLinkField
              contractNumber={formData.sales_contract_number}
              customerName={formData.customer_name}
              language={language}
              onClear={isEdit ? null : () => setFormData((prev) => ({ ...prev, sales_request_id: null, sales_contract_number: '' }))}
            />
          )}

          {/* INSTALL/SERVICE a partir de uma medição (só Admin/GM) */}
          {canMeasure && formData.job_type !== 'MEASURE' && (
            <MeasureLinkField
              value={formData.measure_job_id}
              language={language}
              onPick={(m) => setFormData((prev) => ({ ...prev, ...measureToForm(m) }))}
              onClear={() => setFormData((prev) => ({ ...prev, measure_job_id: null }))}
            />
          )}
          
          <div>
            <Label>{language === 'pt-BR' ? 'Data' : 'Date'} *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Nome do Trabalho' : 'Work Name'} *</Label>
            <Input
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder={language === 'pt-BR' ? 'Ex: John Smith Residence' : 'Ex: John Smith Residence'}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Order Number</Label>
              <Input
                value={formData.order_number}
                onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                placeholder="#4335805"
              />
            </div>
            <div>
              <Label>Tech Measure</Label>
              <Input
                value={formData.tech_measure}
                onChange={(e) => setFormData({ ...formData, tech_measure: e.target.value })}
                placeholder={language === 'pt-BR' ? 'Ex: Matthew Schrock' : 'Ex: Matthew Schrock'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{fromProject ? (language === 'pt-BR' ? 'Horário de chegada *' : 'Arrival time *') : (language === 'pt-BR' ? 'Horário' : 'Time')}</Label>
              <Input
                type="time"
                value={formData.start_time}
                required={!!fromProject}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                data-testid="work-start-time-input"
              />
              {fromProject && formData.start_time && (() => {
                const [h, m] = formData.start_time.split(':').map(Number);
                const f = (hh) => { const d = new Date(2000, 0, 1, hh, m || 0); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
                return <p className="text-[11px] text-indigo-700 mt-1" data-testid="work-arrival-window-hint">✉️ {language === 'pt-BR' ? 'Janela de chegada no e-mail do cliente' : 'Arrival window in the customer email'}: <b>{f(h)} – {f(h + 2)}</b></p>;
              })()}
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Duração (dias)' : 'Duration (days)'}</Label>
              <Input
                type="number"
                min="1"
                value={formData.duration_days}
                onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Portas' : 'Doors'}</Label>
              <Input
                type="number"
                min="0"
                value={formData.doors}
                onChange={(e) => setFormData({ ...formData, doors: e.target.value })}
              />
            </div>
            <div>
              <Label>{language === 'pt-BR' ? 'Janelas' : 'Windows'}</Label>
              <Input
                type="number"
                min="0"
                value={formData.windows}
                onChange={(e) => setFormData({ ...formData, windows: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Endereço' : 'Address'}</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'Cidade' : 'City'}</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Atlanta"
              />
            </div>
            <div>
              <Label>ZIP</Label>
              <Input
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                placeholder="30301"
              />
            </div>
          </div>

          {/* Ano de Construção */}
          <div>
            <Label className="flex items-center gap-2">
              {language === 'pt-BR' ? 'Ano de Construção' : 'Year Built'}
              {formData.year_built && (
                <span className="text-xs text-green-600">🏠 via Zillow</span>
              )}
            </Label>
            <Input
              value={formData.year_built}
              onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
              placeholder="Ex: 1985"
              maxLength={4}
            />
          </div>

          {/* Email do Cliente */}
          <div>
            <Label className="flex items-center gap-2">
              {language === 'pt-BR' ? 'E-mail do Cliente' : 'Customer Email'}
              {formData.customer_email && (
                <span className="text-xs text-green-600">✉️ via PDF</span>
              )}
            </Label>
            <Input
              type="email"
              value={formData.customer_email ?? ''}
              onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              placeholder="cliente@email.com"
              data-testid="work-customer-email-input"
            />
          </div>

          {/* SEÇÃO DE LOGÍSTICA (não se aplica à medição) */}
          {formData.job_type !== 'MEASURE' && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-cyan-800 mb-3 flex items-center gap-2">
              🚚 {language === 'pt-BR' ? 'Informações de Logística' : 'Logistics Information'}
              <span className="text-xs font-normal text-gray-500">
                ({language === 'pt-BR' ? 'Opcional - pode ser preenchido via Excel' : 'Optional - can be filled via Excel'})
              </span>
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'pt-BR' ? 'Cidade de Carregamento' : 'Load City'}</Label>
                <Input
                  value={formData.load_city}
                  onChange={(e) => setFormData({ ...formData, load_city: e.target.value })}
                  placeholder="Lawrenceville"
                />
              </div>
              <div>
                <Label>{language === 'pt-BR' ? 'Porta de Carregamento' : 'Load Door'}</Label>
                <Input
                  value={formData.load_door}
                  onChange={(e) => setFormData({ ...formData, load_door: e.target.value })}
                  placeholder="4"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>{language === 'pt-BR' ? 'Hora de Carregamento' : 'Load Time'}</Label>
                <Input
                  type="time"
                  value={formData.load_time}
                  onChange={(e) => setFormData({ ...formData, load_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Supervisor</Label>
                <Input
                  value={formData.supervisor}
                  onChange={(e) => setFormData({ ...formData, supervisor: e.target.value })}
                  placeholder="Nome do supervisor"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>{language === 'pt-BR' ? 'Coordenador do Projeto' : 'Project Coordinator'}</Label>
                <Input
                  value={formData.project_coordinator}
                  onChange={(e) => setFormData({ ...formData, project_coordinator: e.target.value })}
                  placeholder="Nome do coordenador"
                />
              </div>
              <div>
                <Label>{language === 'pt-BR' ? 'Valor' : 'Amount Due'}</Label>
                <Input
                  type="number"
                  value={formData.amount_due}
                  onChange={(e) => setFormData({ ...formData, amount_due: e.target.value })}
                  placeholder="1500.00"
                />
              </div>
            </div>

            <div className="mt-4">
              <Label>{language === 'pt-BR' ? 'Tipo de Pagamento' : 'Payment Type'}</Label>
              <Input
                value={formData.payment_type}
                onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                placeholder="Credit / Cash / Check"
              />
            </div>

            <div className="mt-4">
              <Label>{language === 'pt-BR' ? 'Financiamento (empresa)' : 'Financing (company)'}</Label>
              <Input
                value={formData.financing}
                onChange={(e) => setFormData({ ...formData, financing: e.target.value })}
                placeholder="Greensky / GoodLeap"
                data-testid="work-financing-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'pt-BR' ? 'Vazio = Cash / Credit Card / Check' : 'Empty = Cash / Credit Card / Check'}
              </p>
            </div>
          </div>
          )}

          {/* MEASURE: notas da medição + arquivos (PDF, fotos, etc.) */}
          {formData.job_type === 'MEASURE' && (
            <div className="border-t pt-4 mt-4 space-y-4" data-testid="work-measure-section">
              <div>
                <Label>📏 {language === 'pt-BR' ? 'Notas da medição' : 'Measurement notes'}</Label>
                <Textarea
                  value={formData.measure_notes}
                  onChange={(e) => setFormData({ ...formData, measure_notes: e.target.value })}
                  placeholder={language === 'pt-BR' ? 'Medidas, observações da casa, acabamentos, acesso, detalhes para a instalação…' : 'Measurements, house notes, trims, access, details for the install…'}
                  className="min-h-[120px] resize-y"
                  data-testid="work-measure-notes"
                />
              </div>
              <MeasureFilesField value={formData.measure_files} onChange={(v) => setFormData((prev) => ({ ...prev, measure_files: v }))} language={language} />
            </div>
          )}

          {/* Upload Installation Package (apenas INSTALL) */}
          {formData.job_type === 'INSTALL' && (
            <div>
              <Label>Installation Package (PDF) - {language === 'pt-BR' ? 'Opcional' : 'Optional'}</Label>
            <div className="mt-2">
              {formData.draft_pdf_url ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <span className="text-sm text-emerald-700">📄 PDF carregado</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, draft_pdf_url: '' })}
                    className="ml-auto"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                    id="pdf-upload"
                    data-testid="work-pdf-upload-input"
                  />
                  <label
                    htmlFor="pdf-upload"
                    className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-zinc-300 rounded-lg cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                  >
                    <Upload className="h-5 w-5 text-zinc-400" />
                    <span className="text-sm text-zinc-600">
                      {uploadingPdf ? (language === 'pt-BR' ? '⏳ Enviando PDF...' : '⏳ Uploading PDF...') : (language === 'pt-BR' ? 'Clique para fazer upload' : 'Click to upload')}
                    </span>
                  </label>
                  {pdfUploadError && (
                    <p className="text-sm text-red-600 mt-2" data-testid="work-pdf-upload-error">
                      ⚠️ {pdfUploadError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Campo de Notas para SERVICE */}
          {formData.job_type === 'SERVICE' && (
            <div>
              <Label>{language === 'pt-BR' ? 'Notas do Serviço' : 'Service Notes'}</Label>
              <Textarea
                value={formData.service_notes}
                onChange={(e) => setFormData({ ...formData, service_notes: e.target.value })}
                placeholder={language === 'pt-BR' ? 
                  'Descreva detalhes do serviço, problema encontrado, ações tomadas...' : 
                  'Describe service details, issues found, actions taken...'}
                className="min-h-[150px] resize-y"
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'pt-BR' ? 
                  'Use este campo para documentar o serviço realizado.' :
                  'Use this field to document the service performed.'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="work-save-btn">
            {loading ? (language === 'pt-BR' ? 'Salvando...' : 'Saving...') : (language === 'pt-BR' ? '✅ Salvar' : '✅ Save')}
          </Button>
          {formData.job_type === 'INSTALL' && formData.draft_pdf_url && (
            <Button onClick={handleSaveWithAI} disabled={loading || uploadingPdf} className="bg-purple-600 hover:bg-purple-700" data-testid="work-save-ai-btn">
              {language === 'pt-BR' ? '🤖 Salvar com IA' : '🤖 Save with AI'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal de Conflitos de Campos do PDF */}
    <Dialog open={showConflictModal} onOpenChange={setShowConflictModal}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-amber-800">
            ⚠️ {language === 'pt-BR' ? 'Conflitos Detectados no PDF' : 'PDF Conflicts Detected'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {language === 'pt-BR' ? 
              'Alguns campos já têm valores diferentes do PDF. Escolha o que fazer com cada campo:' :
              'Some fields have different values from the PDF. Choose what to do with each field:'}
          </p>

          {pdfConflicts.map((conflict, idx) => (
            <div key={idx} className="border border-amber-200 rounded-lg p-4 bg-amber-50">
              <div className="font-semibold text-sm text-amber-900 mb-2">
                {conflict.field_label}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-zinc-300 rounded p-2">
                  <div className="text-xs text-zinc-500 mb-1">
                    {language === 'pt-BR' ? 'Valor Atual:' : 'Current Value:'}
                  </div>
                  <div className="font-semibold text-zinc-900">{conflict.current_value || '(vazio)'}</div>
                </div>
                
                <div className="bg-white border border-cyan-300 rounded p-2">
                  <div className="text-xs text-cyan-600 mb-1">
                    {language === 'pt-BR' ? 'Valor do PDF:' : 'PDF Value:'}
                  </div>
                  <div className="font-semibold text-cyan-900">{conflict.pdf_value}</div>
                </div>
              </div>
              
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Marcar campo para substituir
                    const updatedConflicts = [...pdfConflicts];
                    updatedConflicts[idx].decision = 'replace';
                    setPdfConflicts(updatedConflicts);
                  }}
                  className={`flex-1 ${
                    conflict.decision === 'replace' ? 'bg-cyan-100 border-cyan-400' : ''
                  }`}
                >
                  ✅ {language === 'pt-BR' ? 'Usar PDF' : 'Use PDF'}
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Marcar campo para pular
                    const updatedConflicts = [...pdfConflicts];
                    updatedConflicts[idx].decision = 'skip';
                    setPdfConflicts(updatedConflicts);
                  }}
                  className={`flex-1 ${
                    conflict.decision === 'skip' ? 'bg-zinc-100 border-zinc-400' : ''
                  }`}
                >
                  ⏭️ {language === 'pt-BR' ? 'Manter Atual' : 'Keep Current'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => {
              // Aplicar decisões com todos = skip
              handlePdfConflictResolution('skip_all');
            }}
          >
            {language === 'pt-BR' ? 'Manter Tudo' : 'Keep All'}
          </Button>
          
          <Button
            onClick={() => {
              // Aplicar decisões com todos = replace
              handlePdfConflictResolution('replace_all');
            }}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            {language === 'pt-BR' ? 'Usar Tudo do PDF' : 'Use All from PDF'}
          </Button>
          
          <Button
            onClick={() => {
              // Aplicar decisões individuais
              handlePdfConflictResolution('individual');
            }}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {language === 'pt-BR' ? 'Aplicar Escolhas' : 'Apply Choices'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal de Progresso da IA */}
    <AIProgressModal
      isOpen={showAIModal}
      onClose={() => setShowAIModal(false)}
      logs={aiLogs}
      isComplete={aiComplete}
      extractedData={aiExtractedData}
      language={language}
    />
    </>
  );
};

export default AddEditWorkModal;
